import { useState, type ReactElement } from 'react';
import { toast } from '../../../design-system/components';
import { apiErrorCode, emberApi, type ReadNote } from '../api';
import { trackFailed } from '../../../../../shared-web/track';
import { timeAgo } from '../format';
import './ReadNotes.css';

/** The fixed words of the reader's one-tap thank-you (shares.js SHARE.THANKS_TEXT). */
export const THANKS_TEXT = 'Thank you.';

/** The note's max length (shares.js SHARE.NOTE_MAX_CHARS). */
export const NOTE_MAX_CHARS = 600;

/** How the other side is named on this side's paper. Both stay anonymous. */
export const OTHER_SIDE: Record<'reader' | 'owner', string> = {
  reader: 'Someone who read it',
  owner: 'The person who told it',
};

/**
 * The notes on one read (§4.9, 2026-09-27): a sticky-note thread between
 * the one reader and the one teller. This side's notes stand on the right
 * as "You"; the other side's on the left under its anonymous name. A note
 * the other side wrote that this side has not looked at yet is lit.
 */
export function NoteThread({ notes, me }: { notes: ReadNote[]; me: 'reader' | 'owner' }): ReactElement | null {
  if (notes.length === 0) return null;
  const other = me === 'reader' ? 'owner' : 'reader';
  return (
    <ol className="read-notes" aria-label="Notes">
      {notes.map((n) => (
        <li key={n.id} className={`read-note${n.by === me ? ' is-mine' : ''}${n.by === other && !n.seen ? ' is-new' : ''}`}>
          <span className="read-note-by">{n.by === me ? 'You' : OTHER_SIDE[other]}</span>
          <p className="read-note-text" dir="auto">{n.text}</p>
          <span className="read-note-at">{timeAgo(n.at)}</span>
        </li>
      ))}
    </ol>
  );
}

/** What to say when a note did not go through, by the server's reason. */
export function noteFailureText(code: string): string {
  switch (code) {
    case 'text_rejected': return 'That note can’t go through as written. Say it another way.';
    case 'moderation_unavailable': return 'Couldn’t check that note right now. Try again in a moment.';
    case 'too_long': return 'A little shorter, please.';
    case 'closed': return 'This was closed. Nothing more can be sent.';
    default: return 'Couldn’t send that. Try again.';
  }
}

/**
 * The small composer under a note thread: a couple of lines on the paper,
 * one quiet Send. `onNote` receives the note the server kept.
 */
export function NoteComposer({ readId, placeholder, logState, emberId, onNote }: {
  readId: string;
  placeholder: string;
  /** Which side is writing, on read_note_send. */
  logState: 'reader' | 'owner';
  emberId?: string;
  onNote: (note: ReadNote) => void;
}): ReactElement {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState('');
  const trimmed = text.trim();

  const send = async (): Promise<void> => {
    if (busy || !trimmed) return;
    setBusy(true);
    setProblem('');
    try {
      const { note } = await emberApi.addReadNote(readId, trimmed);
      setText('');
      onNote(note);
    } catch (err) {
      const code = apiErrorCode(err) || 'error';
      setProblem(noteFailureText(code));
      trackFailed('read_note_send', code, emberId ? { ember_id: emberId } : {});
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="read-note-form" onSubmit={(e) => { e.preventDefault(); void send(); }}>
      <textarea
        className="read-note-field"
        placeholder={placeholder}
        value={text}
        onChange={(e) => { setText(e.target.value); if (problem) setProblem(''); }}
        rows={2}
        maxLength={NOTE_MAX_CHARS}
        dir="auto"
        aria-label={placeholder}
      />
      {problem && <p className="read-note-problem" role="alert">{problem}</p>}
      {trimmed && (
        <div className="read-note-foot">
          <button
            type="submit"
            className="read-note-send"
            disabled={busy}
            data-log="read_note_send"
            data-log-state={logState}
            data-log-read-id={readId}
            data-log-ember-id={emberId}
          >
            Send
          </button>
        </div>
      )}
    </form>
  );
}

/**
 * The reader's one tap (§4.9): "Thank you." as a note with fixed words. Gone
 * once a thanks note exists.
 */
export function ThankYouButton({ readId, notes, emberId, onNote }: {
  readId: string;
  notes: ReadNote[];
  emberId?: string;
  onNote: (note: ReadNote) => void;
}): ReactElement | null {
  const [busy, setBusy] = useState(false);
  // Gone once the reader has written anything at all (2026-09-28): the one tap is for someone who says nothing else.
  if (notes.some((n) => n.by === 'reader')) return null;
  const send = async (): Promise<void> => {
    if (busy) return;
    setBusy(true);
    try {
      const { note } = await emberApi.addReadNote(readId, THANKS_TEXT);
      onNote(note);
    } catch (err) {
      const code = apiErrorCode(err) || 'error';
      toast.error(noteFailureText(code));
      trackFailed('read_thanks', code, emberId ? { ember_id: emberId } : {});
    } finally {
      setBusy(false);
    }
  };
  return (
    <button
      type="button"
      className={`ember-btn ember-btn-primary read-thanks${busy ? ' is-busy' : ''}`}
      disabled={busy}
      data-log="read_thanks"
      data-log-read-id={readId}
      data-log-ember-id={emberId}
      onClick={() => { void send(); }}
    >
      Say thank you
    </button>
  );
}
